import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
    when,
} from 'mobx';
import { signOut } from 'aws-amplify/auth';
import { setUser } from '@sentry/react';

import AuthSettingsStore from '../AuthSettingsStore';
import {
    AuthStates,
    CognitoGroups,
    SpecterXUser,
    UserIdentity,
} from '@/types/types';
import { UserInformation } from './interfaces';
import { IS_WSO as IS_WSO_INITIAL } from '../../config/env';
import { BASEURL, ENDPOINTS } from '../../api';
import { captureErrorForSentry, postAuthMessage, sleep } from '../../components/utils';
import { getUserAttributes, getWSOGroups, isIdpAuthUser, tryGetCognitoGroups } from './helpers';
import { IAuthService, IAuthUser } from '@/services/IAuthService';

// TODO: increase this value if MFA cache removing won't work
const LOGOUT_MAX_DELAY_MS = 1_500;

const USER_INFORMATION_INIT: UserInformation = {
    email: '',
    familyName: '',
    givenName: '',
    userId: '',
    displayName: '',
    groups: [],
};

class UserStore {
    constructor(authSettingsStore: AuthSettingsStore, authService?: IAuthService) {
        this.authSettingsStore = authSettingsStore;
        if (IS_WSO_INITIAL && authService) {
            authService.events.addSilentRenewError(async (error) => {
                console.log('silent renew error', error);
                await this.onSilentRenewError();
                await this.login();
            });
            authService.events.addUserSignedOut(async () => {
                await this.resetUser();
            });
            authService.events.addUserSessionChanged(async () => {
                await this.updateSession();
            });
            this.authService = authService;
        }
        makeObservable(this);
    }

    private readonly authSettingsStore: AuthSettingsStore;

    public authService?: IAuthService;

    @observable authState: AuthStates = AuthStates.loading;

    @observable hasDoubleAuthError = false;

    @observable userInformation: UserInformation = { ...USER_INFORMATION_INIT };

    @computed
    get currentUserEmail(): string {
        return this.userInformation?.email;
    }

    @computed
    get currentUserId(): string {
        return this.userInformation?.userId;
    }

    @computed
    get currentUserIdentity(): UserIdentity {
        return {
            id: this.currentUserId,
            email: this.currentUserEmail,
        };
    }

    @computed
    get userGroupsSet(): Set<CognitoGroups> {
        return new Set<CognitoGroups>((this.userInformation?.groups) || []);
    }

    @computed
    get isExternal(): boolean {
        const { userGroupsSet } = this;
        return userGroupsSet.size ? userGroupsSet.has(CognitoGroups.Externals) : true;
    }

    @computed
    get isAuditor(): boolean {
        const { userGroupsSet } = this;
        return userGroupsSet.size ? userGroupsSet.has(CognitoGroups.Auditors) : true;
    }

    @computed
    get isAdmin(): boolean {
        const { userGroupsSet } = this;
        return userGroupsSet.size ? userGroupsSet.has(CognitoGroups.Administrators) : true;
    }

    /** Strict admin check that fails closed (returns false when groups are unknown/empty). */
    @computed
    get isConfirmedAdmin(): boolean {
        return this.userGroupsSet.has(CognitoGroups.Administrators);
    }

    @computed
    get isCollaborator(): boolean {
        const { userGroupsSet } = this;
        return userGroupsSet.size ? userGroupsSet.has(CognitoGroups.Collaborators) : true;
    }

    @computed
    get isUserSetUp(): boolean {
        return !!this.userInformation?.userId;
    }

    private resetUser = async (): Promise<void> => {
        try {
            await this.authService.revokeAccessToken();
        } catch (error) {
            console.log('Error while token revoke', error);
        }
        await this.authService.removeUser();
        await this.authService.clearStaleState();
        this.clearUserInformation();
        this.setAuthState(AuthStates.unauthenticated);
    }

    private updateSession = async (): Promise<void> => {
        const user = await this.authService.getUser();
        console.log('session has been updated, user is', user);
        if (user) {
            await this.setUserInformation(user);
            await this.authService.clearStaleState();
        } else {
            await this.resetUser();
        }
    }

    subscribeOnceOnSetUser = (): Promise<void> => (
        when(() => !!this.currentUserEmail)
    );

    setUserInformation = async (user: SpecterXUser): Promise<void> => {
        const { API } = this.authSettingsStore;
        const { IS_WSO } = this.authSettingsStore;

        const userAttributes = await getUserAttributes(user, IS_WSO);

        /* eslint-disable camelcase */
        const { email, preferred_username, family_name, given_name, sub } = userAttributes;
        const displayName = `${given_name || ''} ${family_name || ''}`.trim() || sub;
        const userEmail = email || preferred_username || '';

        let userGroups: CognitoGroups[] = [];

        if (isIdpAuthUser(user, IS_WSO)) {
            let userMaxRole: CognitoGroups | null = await this.getUserMaxRole();

            if (userMaxRole !== null) {
                userGroups = [userMaxRole];
            }

        } else {
            userGroups = await tryGetCognitoGroups();
        }

        /* eslint-enable camelcase */
        runInAction(() => {
            this.userInformation = {
                email: userEmail,
                familyName: family_name,
                givenName: given_name,
                displayName,
                userId: sub,
                groups: userGroups,
            };
        });

        setUser({
            id: sub,
            email: userEmail,
            username: userEmail,
        });

    }

    @action
    clearUserInformation = (): void => {
        this.userInformation = { ...USER_INFORMATION_INIT };
        setUser(null);
    }

    @action
    setDoubleAuthError = (val: boolean): void => {
        this.hasDoubleAuthError = val;
    }

    onSilentRenewError = async (): Promise<void> => {
        console.log('on Silent renew called');
        await this.resetUser();
    }

    login = async (): Promise<IAuthUser | void> => {
        const user = await this.authService.login();
        if (user) {
            await this.setUserInformation(user);
            this.setAuthState(AuthStates.authenticated);
        }
        return user;
    }

    /**
     * Refresh the Kerberos token.
     */
    renewToken = async (): Promise<void> => {
        const user = await this.authService.refreshToken();
        await this.setUserInformation(user);
        this.setAuthState(AuthStates.authenticated);
    }

    /**
     * Continue login after redirect.
     * Note: Kerberos doesn't use redirects, but this is kept for API compatibility.
     * It simply calls login() which handles Kerberos authentication transparently.
     */
    continueLogin = async (): Promise<void> => {
        if (this.authService.handleRedirectCallback) {
            const user = await this.authService.handleRedirectCallback();
            await this.setUserInformation(user);
            this.setAuthState(AuthStates.authenticated);
        } else {
            await this.login();
        }
        await this.authService.clearStaleState();
    }

    private async saveUserInfo(user: IAuthUser): Promise<void> {
        await this.setUserInformation(user);
    }

    /**
     * Logout - clears local session.
     * Note: Kerberos doesn't have a server-side logout endpoint,
     * so we only clear client-side state.
     */
    logout = async (): Promise<void> => {
        await this.authService.logout();
        this.clearUserInformation();
        this.setAuthState(AuthStates.unauthenticated);
    }

    /**
     * Complete logout after redirect.
     * Note: Kerberos doesn't use redirects, kept for API compatibility.
     */
    completeLogout = async (): Promise<void> => {
        if (this.authService.handleLogoutCallback) {
            await this.authService.handleLogoutCallback();
        }
    }

    @action
    setAuthState = (newState: AuthStates): void => {
        this.authState = newState;
    }

    cognitoSignOut = async (): Promise<void> => {
        // This is experimental solution. It could not work with slow network
        await Promise.race<void>([this.tryRemoveUserCache(), sleep(LOGOUT_MAX_DELAY_MS)]);
        try {
            postAuthMessage({ type: 'signOut' });
            localStorage.setItem('signedOut', 'true');
            await signOut();
            this.setAuthState(AuthStates.unauthenticated);
        } catch (error) {
            console.log('sign out fail', error);
        }
    }

    private async tryRemoveUserCache(): Promise<void> {
        const { currentUserId } = this;
        const { API, skipMFA } = this.authSettingsStore;
        if (!skipMFA && currentUserId) {
            try {
                await API.del(BASEURL.backend(), ENDPOINTS.deleteSessionCache(), {});
            } catch (error) {
                console.log('Could not delete session cache', error);
                captureErrorForSentry(error, 'AuthSettingsStore.tryRemoveUserCache');
            }
        }
    }

    private async getUserMaxRole(): Promise<CognitoGroups | null> {
        const { API } = this.authSettingsStore;

        try {
            const resp: { role: string } = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getUserMaxRole(),
                {},
            );

            const maxRole = resp.role as CognitoGroups;

            if (Object.values(CognitoGroups).includes(maxRole)) {
                return maxRole;
            } else {
                console.warn(`User does not have any valid role`);
                return null;
            }

        } catch (e: any) {

            if (e.response?.status === 404) {
                console.warn('User does not have any role')
                return null;
            }

            console.error(`Failed to get user max role. Reason - ${e}`);
            return null;
        }
    }

}

export default UserStore;
